import { Check, X } from 'lucide-react';
import type { FundedObjectives as FundedObjectivesData } from '@pnl/shared';
import { formatBrl, formatDateBR, formatPct } from '../../lib/format';
import { Card } from './Card';

interface Props {
  data: FundedObjectivesData;
}

// Cada linha é um grid INDEPENDENTE. Duas armadilhas de alinhamento resolvidas:
//  1) 3ª coluna com largura FIXA (não `auto`) — senão o conteúdo (texto "Resumo"
//     no header vs ícone/traço nas linhas) muda a largura por linha.
//  2) `minmax(0,fr)` em vez de `fr` puro (= minmax(auto,fr)) — senão o min-content
//     do texto de cada linha altera a distribuição e desalinha o header dos valores.
// Colunas idênticas em toda linha => header "Resultado" bate com os valores à direita.
const ROW = 'grid grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_3rem] items-center gap-4';
const TEAL_STRIPES =
  'repeating-linear-gradient(45deg, #00C9A7 0, #00C9A7 5px, #00B295 5px, #00B295 10px)';
const GREY_STRIPES =
  'repeating-linear-gradient(45deg, #3A3F52 0, #3A3F52 5px, #2E3143 5px, #2E3143 10px)';

function StatusIcon({ passed }: { passed: boolean }) {
  return passed ? (
    <span className="bg-success-500 grid h-7 w-7 place-items-center rounded-md text-white">
      <Check size={15} strokeWidth={2.5} />
    </span>
  ) : (
    <span className="bg-danger-500 grid h-7 w-7 place-items-center rounded-md text-white">
      <X size={15} strokeWidth={2.5} />
    </span>
  );
}

function Row({ title, value, status }: { title: string; value: string; status?: boolean }) {
  return (
    <div className={ROW + ' border-ink-800 border-b py-3.5 last:border-b-0'}>
      <p className="text-brand-500 text-sm font-semibold">{title}</p>
      <div className="text-ink-100 whitespace-nowrap text-right text-sm font-semibold tabular-nums">
        {value}
      </div>
      <div className="justify-self-end">
        {status === undefined ? (
          <span className="text-ink-600 text-sm">—</span>
        ) : (
          <StatusIcon passed={status} />
        )}
      </div>
    </div>
  );
}

/**
 * Painel de objetivos do Funded Pro (colchão financeiro). Substitui os objetivos
 * do Challenge quando a conta está em FUNDED/LIVE.
 */
export function FundedObjectives({ data }: Props) {
  return (
    <Card title="Objetivos · Funded Pro">
      <div className="px-5 py-2">
        <div
          className={ROW + ' border-ink-800 text-2xs text-ink-400 border-b pb-2.5 pt-1 font-medium'}
        >
          <span>Objetivos de Trading</span>
          <span className="justify-self-end">Resultado</span>
          <span className="justify-self-end">Resumo</span>
        </div>

        <Row title="Lucro acumulado" value={formatBrl(data.accumulatedProfitCents)} />
        <Row
          title="Colchão financeiro"
          value={formatBrl(data.cushionCents)}
          status={data.cushionReached}
        />
        <Row
          title="Disponível para saque"
          value={formatBrl(data.availableCents)}
          status={data.availableCents > 0}
        />
        <Row
          title="Limite de perda diária"
          value={formatBrl(-data.dailyLimitCents)}
          status={data.dailyLimitOk}
        />
        <Row
          title="Dias no lucro (desde o último saque)"
          value={`${data.daysInProfit} / ${data.daysInProfitRequired}`}
          status={data.daysInProfitMet}
        />
        <Row title="Próxima janela de saque" value={formatDateBR(data.nextWindowAt)} />

        {/* Consistência 40% — barra dual listrada */}
        <div className="py-3.5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-brand-500 text-sm font-semibold">
                Consistência: Max {data.consistencyLimitPct}%
              </p>
              <p className="text-2xs text-ink-400 mt-1 leading-relaxed">
                Nenhum dia pode passar de {data.consistencyLimitPct}% do lucro dos dias positivos.
              </p>
            </div>
            <StatusIcon passed={data.consistencyOk} />
          </div>
          <div className="mt-3 flex h-2.5 overflow-hidden rounded-sm">
            <div
              style={{
                width: `${Math.min(100, data.consistencyPct)}%`,
                backgroundImage: TEAL_STRIPES,
                borderRight: '2px solid rgba(255,255,255,0.85)',
              }}
            />
            <div
              style={{
                width: `${Math.max(0, 100 - data.consistencyPct)}%`,
                backgroundImage: GREY_STRIPES,
              }}
            />
          </div>
          <p className="text-2xs text-ink-400 mt-2">
            Melhor dia{' '}
            <span className="text-success-300 font-semibold">
              {formatPct(data.consistencyPct, 2)}
            </span>{' '}
            dos lucros
          </p>
        </div>
      </div>
    </Card>
  );
}
